import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { SmartLink } from "./SmartLink";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X, ArrowRight, BookOpen, Sparkles, Newspaper, Wrench, Building2, Mic } from "lucide-react";
import { SERVICES, SITE_FAQ, FAQBNB_EXCERPTS, scoreMatch } from "../data/searchIndex";
import { algoliasearch } from "algoliasearch";

const API = `${window.__BACKEND__}/api`;

// Algolia client — lazy-init only if configured. Safe to expose the SEARCH-only
// key on the frontend (Algolia design: search keys are read-only and scoped).
const ALGOLIA_APP_ID = process.env.REACT_APP_ALGOLIA_APP_ID;
const ALGOLIA_SEARCH_KEY = process.env.REACT_APP_ALGOLIA_SEARCH_KEY;
const ALGOLIA_INDEX = process.env.REACT_APP_ALGOLIA_INDEX || "vita_da_host";
const algoliaClient = (ALGOLIA_APP_ID && ALGOLIA_SEARCH_KEY)
  ? algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY)
  : null;

// Rotating placeholders shown when input is empty.
// Kept short on purpose: at 260px mobile width even text-sm wraps for long
// strings — short claims read better and feel more like Google's UX.
const PLACEHOLDERS = [
  "Cosa cerchi?",
  "Es. CIN",
  "Es. cedolare",
  "Es. self check-in",
  "Es. tassa soggiorno",
];

// Strip HTML tags from the blog excerpts so we get clean text in the dropdown.
function stripHtml(s) {
  if (!s) return "";
  return String(s)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function highlight(text, query) {
  if (!query || !text) return text;
  const q = query.trim().toLowerCase();
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q);
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark
        className="px-0.5 rounded-sm font-semibold"
        style={{
          background: "rgba(255, 79, 207, 0.28)",
          color: "#ff7adb",
          textShadow: "0 0 8px rgba(255, 79, 207, 0.55)",
        }}
      >
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

export default function GlobalSearchBar({ light = false }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [extraSections, setExtraSections] = useState([]);
  const [popular, setPopular] = useState([]);
  const [phIdx, setPhIdx] = useState(0);
  const [algoliaHits, setAlgoliaHits] = useState([]);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const mobileInputRef = useRef(null);

  // Algolia debounced search (only if configured + query >= 2 chars).
  useEffect(() => {
    if (!algoliaClient) return undefined;
    const q = query.trim();
    if (q.length < 2) {
      setAlgoliaHits([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await algoliaClient.search({
          requests: [{
            indexName: ALGOLIA_INDEX,
            query: q,
            hitsPerPage: 8,
          }],
        });
        if (cancelled) return;
        const hits = res?.results?.[0]?.hits || [];
        setAlgoliaHits(hits);
      } catch (e) {
        // Silent: curated fallback handles the search if Algolia is down
      }
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  // Rotate placeholder every 2.6s while empty + closed.
  useEffect(() => {
    if (query) return undefined;
    const t = setInterval(() => setPhIdx((i) => (i + 1) % PLACEHOLDERS.length), 2600);
    return () => clearInterval(t);
  }, [query]);

  // Fetch blog feed once on mount (lightweight; reused for live filter).
  useEffect(() => {
    let aborted = false;
    fetch(`${API}/blog-news/feed?limit=30`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (aborted || !data?.items) return;
        const items = data.items.map((it) => ({
          title: it.title || "",
          excerpt: stripHtml(it.excerpt || it.description || ""),
          link: it.is_internal && it.slug ? `/blog/${it.slug}` : it.link,
          external: !it.is_internal,
        }));
        setBlogPosts(items);
      })
      .catch(() => {});
    return () => { aborted = true; };
  }, []);

  // Auto-discover any new site section from the live sitemap (cached 10min).
  // Curated SERVICES win on the URL collision; the dynamic ones simply make
  // sure that brand-new pages are indexable without code changes.
  useEffect(() => {
    let aborted = false;
    const curatedUrls = new Set(SERVICES.map((s) => s.url));
    fetch(`${API}/search/sections`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (aborted || !data?.items) return;
        const fresh = data.items
          .filter((it) => !curatedUrls.has(it.url))
          .map((it) => ({
            title: it.title,
            desc: it.category ? `Sezione: ${it.category}` : "Pagina del sito",
            url: it.url,
            tags: [it.slug, it.category].filter(Boolean),
          }));
        setExtraSections(fresh);
      })
      .catch(() => {});
    return () => { aborted = true; };
  }, []);

  // Compute results FIRST — both the logger and logClick depend on it.
  const results = useMemo(() => {
    const q = query.trim();
    if (q.length < 2) return { services: [], blog: [], faq: [], total: 0 };

    const allServices = [...SERVICES, ...extraSections];
    const services = allServices
      .map((s) => ({
        ...s,
        score: scoreMatch(`${s.title} ${s.desc} ${(s.tags || []).join(" ")}`, q),
      }))
      .filter((s) => s.score >= 0.4)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);

    const blog = blogPosts
      .map((p) => ({ ...p, score: scoreMatch(`${p.title} ${p.excerpt}`, q) }))
      .filter((p) => p.score >= 0.4)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);

    const siteFaq = SITE_FAQ.map((f) => ({
      kind: "site",
      q: f.q,
      a: f.a,
      url: f.url,
      topic: f.topic,
      score: scoreMatch(`${f.q} ${f.a} ${f.topic}`, q),
    }));
    const bookFaq = FAQBNB_EXCERPTS.map((f) => ({
      kind: "faqbnb",
      q: f.q,
      a: f.a,
      page: f.page,
      topic: f.topic,
      score: scoreMatch(`${f.q} ${f.a} ${f.topic} ${(f.keywords || []).join(" ")}`, q),
    }));
    const faq = [...bookFaq, ...siteFaq]
      .filter((f) => f.score >= 0.4)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);

    // Algolia hits — already filtered by Algolia's typo-tolerant engine.
    // Dedupe vs curated services (same URL) to avoid double rendering.
    const curatedUrls2 = new Set(allServices.map((s) => s.url));
    const algolia = (algoliaHits || [])
      .filter((h) => !curatedUrls2.has(h.url))
      .slice(0, 6);

    return {
      services,
      blog,
      faq,
      algolia,
      total: services.length + blog.length + faq.length + algolia.length,
    };
  }, [query, blogPosts, extraSections, algoliaHits]);

  // Popular shortcuts — top URLs tapped from previous searches (or curated).
  // Joined with the in-memory SERVICES list to surface human titles even if
  // the URL was logged for a non-curated page.
  useEffect(() => {
    let aborted = false;
    const map = new Map([...SERVICES, ...extraSections].map((s) => [s.url, s.title]));
    fetch(`${API}/search/popular?limit=6`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (aborted || !data?.items) return;
        const enriched = data.items
          .map((it) => ({ url: it.url, title: it.title || map.get(it.url) || it.url }))
          .filter((it) => it.title && it.url);
        setPopular(enriched);
      })
      .catch(() => {});
    return () => { aborted = true; };
  }, [extraSections]);

  // Anonymous query logger — debounced 1.2s after typing stops, only when
  // there is a meaningful query (>= 3 chars). Sends the actual results count
  // so the admin dashboard can spot "zero-result" queries (= site gaps).
  const lastLoggedRef = useRef("");
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) return undefined;
    const sig = `${q}|${results.total}`;
    if (sig === lastLoggedRef.current) return undefined;
    const t = setTimeout(() => {
      lastLoggedRef.current = sig;
      fetch(`${API}/search/log`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ q, results: results.total }),
        keepalive: true,
      }).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [query, results.total]);

  // Helper to record a click destination alongside the originating query.
  function logClick(targetUrl) {
    const q = query.trim();
    if (q.length < 3) return;
    fetch(`${API}/search/log`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ q, results: results.total || 1, clicked: targetUrl }),
      keepalive: true,
    }).catch(() => {});
  }

  // Click outside to close.
  useEffect(() => {
    function onDoc(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // ESC clears.
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Lock body scroll while the mobile full-screen overlay is visible — the
  // overlay itself is internally scrollable, so we don't want the page
  // beneath to scroll along.
  useEffect(() => {
    const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 639.98px)").matches;
    const fs = open && (query.trim().length >= 2 || popular.length > 0) && isMobile;
    if (!fs) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open, query, popular.length]);

  const hasFaqBnbHit = results.faq.some((f) => f.kind === "faqbnb");
  const showDropdown = open && (query.trim().length >= 2);
  const showShortcuts = open && query.trim().length < 2 && popular.length > 0;

  // Track mobile breakpoint with a live media query so we can portal the
  // overlays to <body> on mobile (escaping the Hero's stacking context which
  // sits below the navbar's z-50). Desktop keeps inline positioning.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 639.98px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Helper: portal to <body> on mobile, render inline on desktop.
  const portalIfMobile = (node) =>
    isMobile && typeof document !== "undefined"
      ? createPortal(node, document.body)
      : node;

  return (
    <div
      ref={wrapRef}
      className={`relative z-30 ${light ? "w-full sm:max-w-[620px] mx-0 vdh-search-light" : "w-[260px] sm:w-full sm:max-w-[820px] mx-auto"}`}
      data-testid="global-search-wrap"
    >
      {/* Input */}
      {light ? (
        <div className="relative flex items-center gap-2">
          <div className="relative flex-1 flex items-center rounded-[14px]" style={{ border: "1.5px solid #D5D8DE", background: "#F9FAFB" }}>
            <span className="pl-4 pr-1 flex items-center" style={{ color: "#5B616C" }}><Search size={18} /></span>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder="Cerca: CIN, cedolare secca, Alloggiati Web, self check-in…"
              aria-label="Cerca su Vita da Host"
              data-testid="global-search-input"
              className="flex-1 min-w-0 bg-transparent outline-none border-0 py-3.5 pr-3 text-[16px]"
              style={{ color: "#111318" }}
            />
            {query && (
              <button type="button" onClick={() => { setQuery(""); inputRef.current?.focus(); }} aria-label="Pulisci ricerca" data-testid="global-search-clear" className="mr-1 w-8 h-8 rounded-full flex items-center justify-center" style={{ color: "#5B616C" }}>
                <X size={16} />
              </button>
            )}
          </div>
          <button type="button" onClick={() => { inputRef.current?.focus(); setOpen(true); }} data-testid="global-search-cerca" className="h-[52px] px-[22px] rounded-[14px] font-bold text-[16px] flex-none" style={{ background: "#111318", color: "#FFFFFF" }}>
            Cerca
          </button>
        </div>
      ) : (
      <div
        className={`relative flex items-center gap-2 rounded-full border transition-all
          ${open || query
            ? "border-[var(--vdh-pink)]/60 bg-black/55 shadow-[0_0_32px_rgba(255,79,207,0.32)]"
            : "border-white/15 bg-black/40 hover:border-white/25"
          } backdrop-blur-xl`}
      >
        <span className="pl-5 pr-1 flex items-center text-white/55">
          <Search size={18} />
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          aria-label="Cerca su Vita da Host"
          data-testid="global-search-input"
          className="flex-1 bg-transparent outline-none border-0 py-3.5 sm:py-4 text-base text-white placeholder:text-transparent caret-[var(--vdh-pink)] min-w-0"
        />
        {/* Animated rotating placeholder layered on top of the input — fades when typing. */}
        <AnimatePresence mode="wait">
          {!query && (
            <motion.span
              key={phIdx}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.35 }}
              className="pointer-events-none absolute left-11 right-14 text-sm md:text-base text-white/45 font-tech tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
              data-testid="global-search-placeholder"
            >
              {PLACEHOLDERS[phIdx]}
            </motion.span>
          )}
        </AnimatePresence>
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(""); inputRef.current?.focus(); }}
            aria-label="Pulisci ricerca"
            data-testid="global-search-clear"
            className="mr-1 w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        )}
        <span
          className="hidden sm:flex mr-2 px-2.5 py-1 rounded-md border border-white/15 text-[10px] font-tech tracking-[0.2em] text-white/55 uppercase"
          aria-hidden
        >
          AI
        </span>
      </div>
      )}

      {/* Hint line removed per UX request — keeps the CTAs tighter to the bar. */}

      {/* Shortcut panel — visible when the bar is focused but empty. */}
      {portalIfMobile(
      <AnimatePresence>
        {showShortcuts && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.16 }}
            className="
              fixed inset-0 z-[100] rounded-none border-0 bg-[#0b0b14] backdrop-blur-2xl overflow-hidden text-left
              sm:absolute sm:inset-auto sm:left-0 sm:right-0 sm:mt-3 sm:rounded-2xl sm:border sm:border-white/10
              sm:bg-[#0b0b14]/95 sm:shadow-[0_30px_80px_rgba(0,0,0,0.55)]
            "
            data-testid="global-search-shortcuts"
          >
            {/* Mobile sticky header (same pattern as results dropdown) */}
            <div
              className="sm:hidden sticky top-0 z-10 flex items-center gap-2 px-3 py-3 border-b border-white/10 bg-[#0b0b14]/95 backdrop-blur-xl"
              style={{ paddingTop: "calc(env(safe-area-inset-top, 0) + 0.75rem)" }}
            >
              <div className="flex-1 flex items-center gap-2 px-3.5 py-2.5 rounded-full bg-white">
                <Search size={16} className="text-black/55 flex-none" />
                <input
                  ref={mobileInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                  placeholder="Cerca ovunque su Vita da Host…"
                  aria-label="Cerca su Vita da Host (mobile)"
                  data-testid="global-search-mobile-input"
                  className="flex-1 min-w-0 bg-transparent outline-none border-0 text-[16px] text-black placeholder:text-black/45 caret-[var(--vdh-pink)]"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => { setQuery(""); mobileInputRef.current?.focus(); }}
                    aria-label="Pulisci ricerca"
                    data-testid="global-search-mobile-clear"
                    className="flex-none w-7 h-7 rounded-full hover:bg-black/10 flex items-center justify-center text-black/55"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setOpen(false); inputRef.current?.blur(); mobileInputRef.current?.blur(); }}
                aria-label="Chiudi suggerimenti"
                data-testid="global-search-shortcuts-close"
                className="flex-none w-10 h-10 rounded-full bg-white/8 hover:bg-white/14 flex items-center justify-center text-white"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 sm:p-5">
              <div className="hidden sm:flex items-center gap-2 mb-3">
                <Sparkles size={13} className="text-[var(--vdh-yellow)]" />
                <span className="text-[10px] font-tech uppercase tracking-[0.22em] text-[var(--vdh-yellow)]">
                  Più cercati dagli host
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {popular.map((p) => (
                  <Link
                    key={p.url}
                    to={p.url}
                    onClick={() => { logClick(p.url); setOpen(false); }}
                    data-testid={`global-search-shortcut-${p.url}`}
                    className="group flex items-center gap-3 px-3.5 py-3 rounded-xl bg-white/[0.03] border border-white/8 hover:border-[var(--vdh-pink)]/50 hover:bg-white/[0.06] transition-colors"
                  >
                    <span className="w-8 h-8 rounded-full bg-[var(--vdh-pink)]/15 border border-[var(--vdh-pink)]/40 text-[var(--vdh-pink)] flex items-center justify-center flex-none">
                      <ArrowRight size={14} />
                    </span>
                    <span className="flex-1 min-w-0 text-sm font-medium text-white group-hover:text-[var(--vdh-pink)] transition-colors line-clamp-1">
                      {p.title}
                    </span>
                  </Link>
                ))}
              </div>
              <p className="mt-4 text-[11px] text-white/40 font-tech text-center sm:text-left">
                Inizia a digitare per cercare ovunque su Vita da Host.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      )}

      {/* Dropdown */}
      {portalIfMobile(
      <AnimatePresence>
        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.18 }}
            className="
              fixed inset-0 z-[100] rounded-none border-0 bg-[#0b0b14] backdrop-blur-2xl overflow-hidden text-left
              sm:absolute sm:inset-auto sm:left-0 sm:right-0 sm:mt-3 sm:rounded-2xl sm:border sm:border-white/10
              sm:bg-[#0b0b14]/95 sm:shadow-[0_30px_80px_rgba(0,0,0,0.55)]
            "
            data-testid="global-search-dropdown"
          >
            {/* Mobile-only sticky header — editable input (white box) so the
                user always sees what they are typing inside the fullscreen. */}
            <div
              className="sm:hidden sticky top-0 z-10 flex items-center gap-2 px-3 py-3 border-b border-white/10 bg-[#0b0b14]/95 backdrop-blur-xl"
              style={{ paddingTop: "calc(env(safe-area-inset-top, 0) + 0.75rem)" }}
            >
              <div className="flex-1 flex items-center gap-2 px-3.5 py-2.5 rounded-full bg-white">
                <Search size={16} className="text-black/55 flex-none" />
                <input
                  ref={mobileInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                  placeholder="Cerca ovunque su Vita da Host…"
                  aria-label="Cerca su Vita da Host (mobile)"
                  data-testid="global-search-mobile-input"
                  className="flex-1 min-w-0 bg-transparent outline-none border-0 text-[16px] text-black placeholder:text-black/45 caret-[var(--vdh-pink)]"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => { setQuery(""); mobileInputRef.current?.focus(); }}
                    aria-label="Pulisci ricerca"
                    data-testid="global-search-mobile-clear-2"
                    className="flex-none w-7 h-7 rounded-full hover:bg-black/10 flex items-center justify-center text-black/55"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setOpen(false); setQuery(""); inputRef.current?.blur(); mobileInputRef.current?.blur(); }}
                aria-label="Chiudi ricerca"
                data-testid="global-search-close"
                className="flex-none w-10 h-10 rounded-full bg-white/8 hover:bg-white/14 flex items-center justify-center text-white"
              >
                <X size={18} />
              </button>
            </div>

            {results.total === 0 ? (
              <div className="px-5 py-8 text-center" data-testid="global-search-empty">
                <p className="text-sm text-white/70">Nessun risultato per <strong className="text-white">"{query}"</strong>.</p>
                <p className="mt-2 text-xs text-white/50">
                  Prova con <em>CIN</em>, <em>cedolare</em>, <em>self check-in</em>, <em>tassa soggiorno</em>.
                </p>
                <Link
                  to="/servizi/faqbnb"
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--vdh-yellow)] text-black text-xs font-bold uppercase tracking-[0.18em] hover:scale-[1.03] transition-transform"
                  data-testid="global-search-empty-cta"
                  onClick={() => setOpen(false)}
                >
                  <BookOpen size={14} /> FaqBnb 2026 · GRATIS
                </Link>
              </div>
            ) : (
              <div className="max-h-[calc(100vh-72px)] sm:max-h-[70vh] overflow-y-auto overscroll-contain divide-y divide-white/5 pb-6 sm:pb-0">
                {/* Algolia hits — dynamic content (associazioni, blog, podcast) */}
                {results.algolia.length > 0 && (
                  <Section
                    label="Risultati"
                    icon={<Search size={13} />}
                    accent="var(--vdh-pink)"
                    testid="global-search-section-algolia"
                  >
                    {results.algolia.map((h) => {
                      const Ico = h.content_type === "associazione" ? Building2
                        : h.content_type === "podcast" ? Mic
                        : h.content_type === "blog" ? Newspaper
                        : Wrench;
                      const typeLabel = h.content_type === "associazione" ? "Associazione"
                        : h.content_type === "podcast" ? "Podcast"
                        : h.content_type === "blog" ? "Blog"
                        : "Servizio";
                      return (
                        <Link
                          key={h.objectID}
                          to={h.url}
                          onClick={() => { logClick(h.url); setOpen(false); }}
                          data-testid={`global-search-algolia-${h.objectID}`}
                          className="group block px-4 py-3 rounded-xl hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-none w-9 h-9 rounded-lg overflow-hidden bg-black/40 border border-white/10 flex items-center justify-center">
                              {h.image ? (
                                <img src={h.image} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Ico size={15} className="text-white/40" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <span className="text-[9.5px] font-tech uppercase tracking-[0.18em] text-[var(--vdh-pink)]">{typeLabel}</span>
                                {h.comune && (
                                  <span className="text-[9.5px] text-white/35">· {h.comune}</span>
                                )}
                              </div>
                              <h4 className="text-sm font-semibold text-white group-hover:text-[var(--vdh-pink)] transition-colors line-clamp-1">
                                {highlight(h.title, query)}
                              </h4>
                              {h.excerpt && (
                                <p className="text-xs text-white/55 line-clamp-1 mt-0.5">{highlight(h.excerpt, query)}</p>
                              )}
                            </div>
                            <ArrowRight size={14} className="mt-1 text-white/35 group-hover:text-[var(--vdh-pink)] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                          </div>
                        </Link>
                      );
                    })}
                  </Section>
                )}

                {/* Services */}
                {results.services.length > 0 && (
                  <Section
                    label="Servizi"
                    icon={<Wrench size={13} />}
                    accent="var(--vdh-pink)"
                    testid="global-search-section-services"
                  >
                    {results.services.map((s) => (
                      <SmartLink
                        key={s.url}
                        to={s.url}
                        onClick={() => { logClick(s.url); setOpen(false); }}
                        data-testid={`global-search-service-${s.url}`}
                        className="group block px-4 py-3 rounded-xl hover:bg-white/5 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-white group-hover:text-[var(--vdh-pink)] transition-colors line-clamp-1">
                              {highlight(s.title, query)}
                            </h4>
                            <p className="text-xs text-white/55 line-clamp-1 mt-0.5">{highlight(s.desc, query)}</p>
                          </div>
                          <ArrowRight size={14} className="mt-1 text-white/35 group-hover:text-[var(--vdh-pink)] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                        </div>
                      </SmartLink>
                    ))}
                  </Section>
                )}

                {/* FAQ — with FaqBnb upsell at the top of the section if hit */}
                {results.faq.length > 0 && (
                  <Section
                    label="Domande frequenti"
                    icon={<Sparkles size={13} />}
                    accent="var(--vdh-yellow)"
                    testid="global-search-section-faq"
                  >
                    {hasFaqBnbHit && (
                      <Link
                        to="/servizi/faqbnb"
                        onClick={() => { logClick("/servizi/faqbnb"); setOpen(false); }}
                        data-testid="global-search-faqbnb-cta"
                        className="group flex items-center gap-3 px-4 py-3 mb-2 rounded-xl bg-gradient-to-r from-[#ff4fcf]/20 via-[#EA580C]/15 to-[#EA580C]/20 border border-[var(--vdh-yellow)]/40 hover:border-[var(--vdh-yellow)] hover:shadow-[0_0_24px_rgba(234,88,12,0.35)] transition-all"
                      >
                        <span className="w-9 h-9 rounded-full bg-[var(--vdh-yellow)] text-black flex items-center justify-center flex-shrink-0">
                          <BookOpen size={16} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-tech uppercase tracking-[0.2em] text-[var(--vdh-yellow)]">Estratto dal manuale</p>
                          <h4 className="text-sm font-bold text-white leading-tight">FaqBnb 2026 · 67 pagine di risposte concrete</h4>
                        </div>
                        <span className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[var(--vdh-yellow)] text-black text-[11px] font-bold uppercase tracking-[0.14em] group-hover:scale-105 transition-transform">
                          €9,90 <ArrowRight size={12} />
                        </span>
                      </Link>
                    )}
                    {results.faq.map((f, i) => {
                      const isBook = f.kind === "faqbnb";
                      const href = isBook ? "/servizi/faqbnb" : f.url;
                      return (
                        <Link
                          key={`${f.kind}-${i}`}
                          to={href}
                          onClick={() => { logClick(href); setOpen(false); }}
                          data-testid={`global-search-faq-${i}`}
                          className="group block px-4 py-3 rounded-xl hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-start gap-2">
                            <span
                              className={`mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-tech uppercase tracking-[0.14em] flex-shrink-0
                                ${isBook
                                  ? "bg-[var(--vdh-yellow)]/15 text-[var(--vdh-yellow)] border border-[var(--vdh-yellow)]/30"
                                  : "bg-white/5 text-white/60 border border-white/10"
                                }`}
                            >
                              {isBook ? `FaqBnb · p.${f.page}` : f.topic}
                            </span>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-semibold text-white group-hover:text-[var(--vdh-yellow)] transition-colors leading-tight">
                                {highlight(f.q, query)}
                              </h4>
                              <p className="text-xs text-white/60 line-clamp-2 mt-1">{highlight(f.a, query)}</p>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </Section>
                )}

                {/* Blog */}
                {results.blog.length > 0 && (
                  <Section
                    label="Blog & news"
                    icon={<Newspaper size={13} />}
                    accent="#4fffb0"
                    testid="global-search-section-blog"
                  >
                    {results.blog.map((p, i) => {
                      const inner = (
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-white group-hover:text-[#4fffb0] transition-colors line-clamp-2 leading-tight">
                              {highlight(p.title, query)}
                            </h4>
                            {p.excerpt && (
                              <p className="text-xs text-white/55 line-clamp-2 mt-1">{highlight(p.excerpt, query)}</p>
                            )}
                          </div>
                          <ArrowRight size={14} className="mt-1 text-white/35 group-hover:text-[#4fffb0] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                        </div>
                      );
                      return p.external ? (
                        <a
                          key={i}
                          href={p.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => { logClick(p.link); setOpen(false); }}
                          data-testid={`global-search-blog-${i}`}
                          className="group block px-4 py-3 rounded-xl hover:bg-white/5 transition-colors"
                        >
                          {inner}
                        </a>
                      ) : (
                        <Link
                          key={i}
                          to={p.link}
                          onClick={() => { logClick(p.link); setOpen(false); }}
                          data-testid={`global-search-blog-${i}`}
                          className="group block px-4 py-3 rounded-xl hover:bg-white/5 transition-colors"
                        >
                          {inner}
                        </Link>
                      );
                    })}
                  </Section>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      )}
    </div>
  );
}

function Section({ label, icon, accent, children, testid }) {
  return (
    <div className="p-2" data-testid={testid}>
      <div className="px-3 py-2 flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded" style={{ color: accent }}>
          {icon}
        </span>
        <span
          className="text-[10px] font-tech uppercase tracking-[0.22em]"
          style={{ color: accent }}
        >
          {label}
        </span>
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}
